import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";

function LegalLayout({ eyebrow, title, lastUpdate, children }) {
  return (
    <section className="ok-section">
      <div className="ok-container max-w-3xl">
        <div className="ok-eyebrow mb-3">{eyebrow}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">{title}</h1>
        {lastUpdate && <p className="text-xs text-[#7b8e8f] mt-3">Laatst bijgewerkt: {lastUpdate}</p>}
        <div className="mt-10 space-y-6 text-[#0a191a] leading-relaxed">{children}</div>
        <div className="mt-16 border-t border-[#e2e8f0] pt-8 text-sm text-[#4a5d5e] flex items-center gap-2">
          <Mail size={14} /> Vragen? Mail naar <a href="mailto:info@overheidskrachten.nl" className="text-[#01696f] font-semibold hover:underline">info@overheidskrachten.nl</a>
        </div>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold mt-8 mb-3">{title}</h2>
      <div className="text-[15px] text-[#0a191a] space-y-3">{children}</div>
    </div>
  );
}

export function OverOns() {
  return (
    <section className="ok-section">
      <div className="ok-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="ok-eyebrow mb-3">Over ons</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Wij leveren <span className="text-[#01696f]">kracht</span> aan de publieke sector.
            </h1>
            <p className="mt-6 text-lg text-[#4a5d5e] max-w-xl leading-relaxed">
              Overheidskrachten is een Nederlands detacheringsbureau dat ervaren professionals plaatst bij gemeenten, provincies, rijksoverheid en waterschappen.
              Onze recruiters kennen de publieke sector en weten welke profielen passen bij welke opdracht.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="border border-[#e2e8f0] p-5">
                <div className="font-display text-3xl font-bold text-[#01696f]">120+</div>
                <p className="text-sm text-[#4a5d5e] mt-2">Geplaatste professionals bij overheidsorganisaties</p>
              </div>
              <div className="border border-[#e2e8f0] p-5">
                <div className="font-display text-3xl font-bold text-[#01696f]">12</div>
                <p className="text-sm text-[#4a5d5e] mt-2">Provincies waar we actief zijn</p>
              </div>
            </div>
            <h2 className="font-display text-2xl font-semibold mt-12">Onze missie</h2>
            <p className="mt-3 text-[#4a5d5e] leading-relaxed">
              De publieke sector heeft kracht nodig om grote opgaven aan te kunnen — van de Omgevingswet tot digitalisering en
              de energietransitie. Wij verbinden de juiste professionals aan de juiste opdracht, snel en zorgvuldig.
            </p>
            <h2 className="font-display text-2xl font-semibold mt-10">Waarvoor staan we?</h2>
            <ul className="mt-3 space-y-2 text-[#4a5d5e]">
              <li>· Persoonlijk contact en korte lijnen</li>
              <li>· Eerlijke tarieven, transparante voorwaarden</li>
              <li>· Zorgvuldige matching op basis van expertise, motivatie en cultuur</li>
              <li>· AVG-veilige verwerking van kandidaatgegevens</li>
            </ul>
            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/aanmelden-kandidaat" className="inline-flex items-center gap-2 bg-[#01696f] text-white px-6 py-3 rounded-sm hover:bg-[#015257] font-medium">
                Aanmelden als kandidaat <ArrowRight size={16} />
              </Link>
              <Link to="/aanmelden-inlener" className="inline-flex items-center gap-2 border border-[#0a191a] px-6 py-3 rounded-sm hover:bg-[#0a191a] hover:text-white font-medium">
                Aanmelden als inlener
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-[#e2e8f0]">
              <img src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg" alt="Team in overleg" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AlgemeneVoorwaarden() {
  return (
    <LegalLayout eyebrow="Juridisch" title="Algemene Voorwaarden" lastUpdate="februari 2026">
      <p>
        Deze algemene voorwaarden zijn van toepassing op alle diensten van Overheidskrachten B.V. (KvK 000000000), gevestigd in Nederland.
        Door gebruik te maken van onze diensten gaat u akkoord met deze voorwaarden.
      </p>
      <Section title="1. Definities">
        <p><strong>Overheidskrachten:</strong> de besloten vennootschap Overheidskrachten B.V., handelend als detacheringsbureau voor de publieke sector.</p>
        <p><strong>Kandidaat:</strong> de professional die zich registreert om te worden voorgesteld voor overheidsopdrachten.</p>
        <p><strong>Inlener:</strong> de overheidsorganisatie die professionals inhuurt via Overheidskrachten.</p>
      </Section>
      <Section title="2. Diensten">
        <p>Overheidskrachten verleent bemiddelings- en detacheringsdiensten voor de Nederlandse publieke sector. Wij koppelen kandidaten aan opdrachten, hetzij direct, hetzij via aanbestedingsplatforms zoals Flextender.</p>
      </Section>
      <Section title="3. Inschrijving en gebruik">
        <p>Inschrijving als kandidaat of inlener is gratis. U bent zelf verantwoordelijk voor de juistheid van de verstrekte gegevens. Misbruik van het platform leidt tot verwijdering van het account.</p>
      </Section>
      <Section title="4. Aansprakelijkheid">
        <p>Overheidskrachten spant zich in om kandidaten en inleners zorgvuldig te koppelen, maar garandeert geen plaatsing of opdracht. Onze aansprakelijkheid is beperkt tot het bedrag dat in het betreffende dossier in rekening is gebracht.</p>
      </Section>
      <Section title="5. Toepasselijk recht">
        <p>Op alle overeenkomsten met Overheidskrachten is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.</p>
      </Section>
      <p className="text-sm text-[#7b8e8f] italic">
        Dit is een verkorte weergave. De volledige algemene voorwaarden zijn op verzoek beschikbaar via info@overheidskrachten.nl.
      </p>
    </LegalLayout>
  );
}

export function Privacyvoorwaarden() {
  return (
    <LegalLayout eyebrow="Juridisch" title="Privacyvoorwaarden" lastUpdate="februari 2026">
      <p>
        Overheidskrachten B.V. respecteert uw privacy en verwerkt persoonsgegevens conform de Algemene Verordening Gegevensbescherming (AVG/GDPR).
      </p>
      <Section title="1. Welke gegevens verwerken wij?">
        <p>Wij verwerken: NAW-gegevens, contactgegevens, CV, expertisegebied, beschikbaarheid, gewenst tarief of salaris en sollicitatiegeschiedenis. Bij inleners: organisatiegegevens en contactpersoongegevens.</p>
      </Section>
      <Section title="2. Doel van de verwerking">
        <p>Uw gegevens worden uitsluitend gebruikt om u te matchen op passende opdrachten, om met u te communiceren en om wettelijke verplichtingen na te komen.</p>
      </Section>
      <Section title="3. Delen met derden">
        <p>Wij delen uw gegevens uitsluitend met inleners nadat u daartoe expliciete toestemming heeft gegeven, of wanneer dit wettelijk verplicht is.</p>
      </Section>
      <Section title="4. Bewaartermijn">
        <p>Kandidaatgegevens bewaren wij maximaal 2 jaar na het laatste contact, tenzij u langer geregistreerd wenst te blijven. Sollicitatiegegevens worden 4 weken na afronding van de procedure verwijderd.</p>
      </Section>
      <Section title="5. Uw rechten">
        <p>U heeft het recht op inzage, rectificatie en verwijdering van uw gegevens. Stuur uw verzoek naar info@overheidskrachten.nl.</p>
      </Section>
      <Section title="6. Beveiliging">
        <p>Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beschermen, waaronder versleutelde opslag en toegangscontrole.</p>
      </Section>
    </LegalLayout>
  );
}

export function Cookieverklaring() {
  return (
    <LegalLayout eyebrow="Juridisch" title="Cookieverklaring" lastUpdate="februari 2026">
      <p>
        Overheidskrachten gebruikt cookies om de website goed te laten functioneren en gebruikerservaring te verbeteren.
      </p>
      <Section title="1. Wat zijn cookies?">
        <p>Cookies zijn kleine tekstbestanden die door uw browser worden opgeslagen wanneer u onze website bezoekt.</p>
      </Section>
      <Section title="2. Welke cookies gebruiken wij?">
        <p><strong>Functionele cookies:</strong> noodzakelijk voor inlog- en sessiebeheer. Deze plaatsen wij altijd.</p>
        <p><strong>Analytische cookies:</strong> wij gebruiken anonieme analytics om de site te verbeteren. Geen persoonsgegevens worden gedeeld met derden.</p>
        <p>Wij plaatsen geen tracking- of marketingcookies van derden zonder uw expliciete toestemming.</p>
      </Section>
      <Section title="3. Cookies weigeren of verwijderen">
        <p>U kunt cookies weigeren of verwijderen via uw browserinstellingen. Houd er rekening mee dat sommige functies dan niet meer werken.</p>
      </Section>
      <Section title="4. Contact">
        <p>Vragen over deze cookieverklaring? Neem contact op via info@overheidskrachten.nl.</p>
      </Section>
    </LegalLayout>
  );
}
